import { _isNil } from '@/littledash';
import type { Edge } from '@xyflow/react';
import type {
  GraphCanvasConfig,
  GraphCanvasNode,
  GraphCanvasSnapshot,
  GraphChannel,
  LoadStatus,
} from './GraphCanvas.model';

/**
 * Identity layout: positions untouched. Used when no layout is configured. Returns fresh node
 * objects (not just a copied array) to honour the `LayoutFn` contract, so external mutation of the
 * input nodes can't leak into the store.
 */
const identityLayout = <TData extends Record<string, unknown>>(
  nodes: ReadonlyArray<GraphCanvasNode<TData>>
): Array<GraphCanvasNode<TData>> => nodes.map((node) => ({ ...node }));

/** No-edges default: a graph with no derived relationships. */
const noEdges = (): Array<Edge> => [];

/**
 * Generic, framework-agnostic reactive store for a React Flow graph. Holds caller-defined node data
 * (`TData`) and metadata (`TMeta`), and derives edges + positions through injected
 * `deriveEdges`/`layout` hooks — so it knows nothing about the graph's domain or data source.
 *
 * Mutations are independent, not a required sequence: `setGraph` replaces the whole graph,
 * `upsertNodes` adds nodes incrementally (id-deduped), `updateMeta` merges metadata, `setLoadError`
 * flags a failed load. A caller can use any subset — a one-shot static graph (`setGraph` alone), an
 * incrementally-built one (`upsertNodes`), or a snapshot-then-live-updates mix. The store is agnostic
 * to whether updates arrive from a fetch, a stream, polling, or static data.
 *
 * Modelled on the mature `StepFormEngine` pattern: private fields, a single `EventTarget`, and
 * getters that return the stored value BY REFERENCE — so the `useSyncExternalStore` snapshot stays
 * referentially stable between mutations and never loops — with `#notify` dispatching a plain
 * `Event(channel)` per channel so consumers re-render only for the slice they subscribe to.
 */
export class GraphCanvasEngine<TData extends Record<string, unknown>, TMeta extends Record<string, unknown>> {
  readonly #eventTarget = new EventTarget();
  readonly #nodeIds = new Set<string>();
  readonly #deriveEdges: (nodes: ReadonlyArray<GraphCanvasNode<TData>>) => Array<Edge>;
  readonly #layout: (
    nodes: ReadonlyArray<GraphCanvasNode<TData>>,
    edges: ReadonlyArray<Edge>
  ) => Array<GraphCanvasNode<TData>>;
  #nodes: Array<GraphCanvasNode<TData>> = [];
  #edges: Array<Edge> = [];
  #meta: TMeta | null = null;
  #loadStatus: LoadStatus = 'loading';

  constructor(config: GraphCanvasConfig<TData> = {}) {
    this.#deriveEdges = config.deriveEdges ?? noEdges;
    this.#layout = config.layout ?? identityLayout;
  }

  getNodes(): ReadonlyArray<GraphCanvasNode<TData>> {
    return this.#nodes;
  }

  getEdges(): ReadonlyArray<Edge> {
    return this.#edges;
  }

  getMeta(): Readonly<TMeta> | null {
    return this.#meta;
  }

  /**
   * Snapshot-hydration lifecycle: `loading` → `ready` (set by `setGraph`) or `error` (`setLoadError`).
   *
   * IMPORTANT: only `setGraph` flips this to `ready` — `upsertNodes` does NOT. So a consumer that
   * builds a graph purely incrementally (never calling `setGraph`) stays `loading` forever. This is
   * intentional: `loadStatus` tracks *initial hydration*, not "have any nodes arrived". Incremental-only
   * consumers should either call `setGraph({ nodes: [], meta })` once to signal ready, or simply not
   * gate their first paint on `loadStatus`.
   *
   * (Future option, when a real incremental-only consumer exists: add an explicit `markReady()` method
   * so readiness can be signalled without a placeholder empty `setGraph`. Deferred as YAGNI for now.)
   */
  getLoadStatus(): LoadStatus {
    return this.#loadStatus;
  }

  /** One-shot hydration from a snapshot. Derives edges, applies layout, marks load `ready`, notifies. */
  setGraph(snapshot: GraphCanvasSnapshot<TData, TMeta>): void {
    this.#edges = this.#deriveEdges(snapshot.nodes);
    this.#nodes = this.#layout(snapshot.nodes, this.#edges);
    this.#meta = snapshot.meta;
    this.#nodeIds.clear();
    this.#nodes.forEach((node) => this.#nodeIds.add(node.id));
    this.#loadStatus = 'ready';
    this.#notify('nodes');
    this.#notify('edges');
    this.#notify('meta');
    this.#notify('load');
  }

  /**
   * Appends nodes incrementally (e.g. from a live stream). Idempotent by node id — nodes already
   * present are skipped, and duplicate ids *within* a single batch collapse to one, so snapshot/stream
   * overlap is a silent no-op and a wholly-duplicate batch notifies nothing. Re-derives edges and
   * re-runs layout over the resulting node set.
   *
   * Cost: each call re-derives edges and re-lays out the ENTIRE node set, so streaming n nodes one at
   * a time is ~O(n²) overall. This is a deliberate simplicity/determinism trade — the target graphs are
   * small (tens of nodes). If a future consumer streams large graphs, make derivation/layout
   * incremental (position only the new node relative to its parent) or coalesce bursts per frame.
   */
  upsertNodes(incoming: ReadonlyArray<GraphCanvasNode<TData>>): void {
    const fresh = incoming.reduce<Array<GraphCanvasNode<TData>>>((acc, node) => {
      if (!this.#nodeIds.has(node.id)) {
        this.#nodeIds.add(node.id);
        acc.push(node);
      }
      return acc;
    }, []);
    if (fresh.length === 0) {
      return;
    }
    const nextNodes = [...this.#nodes, ...fresh];
    this.#edges = this.#deriveEdges(nextNodes);
    this.#nodes = this.#layout(nextNodes, this.#edges);
    this.#notify('nodes');
    this.#notify('edges');
  }

  /**
   * Merges a partial metadata update. No-op before hydration (`#meta` is null until `setGraph`) —
   * callers that may receive a meta update before the snapshot resolves must buffer it and re-apply
   * after `setGraph`. Always notifies when applied (equality is the caller's concern via the partial).
   */
  updateMeta(partial: Partial<TMeta>): void {
    if (_isNil(this.#meta)) {
      return;
    }
    this.#meta = { ...this.#meta, ...partial };
    this.#notify('meta');
  }

  /** Marks hydration as failed so consumers can surface an error instead of an empty canvas. */
  setLoadError(): void {
    if (this.#loadStatus === 'error') {
      return;
    }
    this.#loadStatus = 'error';
    this.#notify('load');
  }

  /** Subscribe to a channel; returns an unsubscribe function. */
  subscribe(channel: GraphChannel, listener: () => void): () => void {
    this.#eventTarget.addEventListener(channel, listener);
    return () => this.#eventTarget.removeEventListener(channel, listener);
  }

  #notify(channel: GraphChannel): void {
    this.#eventTarget.dispatchEvent(new Event(channel));
  }
}
